import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${window.__BACKEND__}/api`;
const TICKER_SPEED_PX_S = 85;

let CACHE = { items: [], at: 0 };

export default function NewsTicker() {
  const [items, setItems] = useState(CACHE.items);
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(30);
  const [cloned, setCloned] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const needsFetch = Date.now() - CACHE.at > 60 * 60 * 1000;
    if (!needsFetch && CACHE.items.length) return;
    axios
      .get(`${API}/news/feed`)
      .then((r) => {
        if (!cancelled && Array.isArray(r.data?.items) && r.data.items.length) {
          CACHE = { items: r.data.items, at: Date.now() };
          setItems(r.data.items);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // SEO: raw HTML renders each headline ONCE; the clone copy for the seamless
  // marquee is appended after mount, aria-hidden + tabindex=-1.
  useEffect(() => {
    if (!items.length) return;
    const id = requestAnimationFrame(() => setCloned(true));
    return () => cancelAnimationFrame(id);
  }, [items.length]);

  useEffect(() => {
    if (!items.length || !trackRef.current || !cloned) return;
    const recompute = () => {
      const el = trackRef.current;
      if (!el) return;
      const oneCopy = el.scrollWidth / 2;
      if (oneCopy <= 0) return;
      setDuration(Math.max(18, Math.round(oneCopy / TICKER_SPEED_PX_S)));
    };
    recompute();
    window.addEventListener("resize", recompute);
    const t = setTimeout(recompute, 400);
    return () => {
      window.removeEventListener("resize", recompute);
      clearTimeout(t);
    };
  }, [items, cloned]);

  if (!items.length) return null;

  const Item = ({ it, clone }) => (
    <a
      href={it.link}
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-hidden={clone ? "true" : undefined}
      tabIndex={clone ? -1 : undefined}
      data-testid={!clone && it === items[0] ? "news-ticker-link" : undefined}
      className="inline-flex items-center gap-3 px-5 text-[14px] whitespace-nowrap"
      style={{ color: "#FFFFFF" }}
    >
      <span>{it.title}{it.source ? ` · ${it.source}` : ""}</span>
      <span style={{ color: "rgba(255,255,255,0.5)" }}>/</span>
    </a>
  );

  return (
    <div
      className="sticky z-40"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 72px)", background: "#CF0A0A", color: "#FFFFFF" }}
      data-testid="news-ticker"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 h-11 flex items-center gap-4 overflow-hidden">
        <Link
          to="/hostnews"
          data-testid="news-ticker-hostnews-link"
          title="Apri HostNews — il giornale degli affitti brevi"
          className="flex-none font-extrabold text-[12px] px-2.5 py-1 rounded-md"
          style={{ background: "#FFD23F", color: "#111318", letterSpacing: "0.04em" }}
        >
          HOSTNEWS
        </Link>

        <div
          className="flex-1 overflow-hidden relative h-full flex items-center"
          style={{
            maskImage: "linear-gradient(to right, transparent 0, #000 30px, #000 calc(100% - 30px), transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0, #000 30px, #000 calc(100% - 30px), transparent 100%)",
          }}
        >
          <div
            ref={trackRef}
            className={`${cloned ? "ticker-track" : ""} flex items-center h-full`}
            style={{ width: "max-content", animationDuration: `${duration}s` }}
          >
            {items.map((it, i) => <Item key={i} it={it} />)}
            {cloned && items.map((it, i) => <Item key={`c-${i}`} it={it} clone />)}
          </div>
        </div>

        <Link
          to="/hostnews"
          className="flex-none font-bold text-[14px] hidden sm:block"
          style={{ color: "#FFFFFF" }}
          data-testid="news-ticker-rassegna"
        >
          Rassegna completa →
        </Link>
      </div>
    </div>
  );
}
